// How Signals looks: a colour family per category, and small formatters. The server owns what a signal *is*
// (label, description, example, conditions, cost); this file only decides how it is dressed.

/**
 * Signal families: one colour each (see signals.scss) and the name the Library shows. The server's category names
 * vary ("People", "Company", "Engagement"…), so a family is matched by words in the category.
 */
export const FAMILIES = [
  { key: "ppl", label: "People moves", match: /people|person|job|role/ },
  { key: "acc", label: "Accounts", match: /company|account/ },
  { key: "cmp", label: "Competitors", match: /competitor/ },
  { key: "evt", label: "Events", match: /event/ },
  { key: "own", label: "Your own activity", match: /engagement|activity|own/ },
  { key: "cus", label: "Your custom signals", match: /custom/ }
];
const OWN = FAMILIES[4];
// Competitor and event words win over the broader people/company ones ("Competitor accounts" is a competitor signal).
const MATCH_ORDER = [FAMILIES[5], FAMILIES[2], FAMILIES[3], FAMILIES[0], FAMILIES[1], OWN];

export const familyOf = category => MATCH_ORDER.find(f => f.match.test(String(category || "").toLowerCase())) || OWN;
export const categoryClass = category => familyOf(category).key;

export const entityNoun = entityType => (entityType === "ACCOUNT" ? "account" : "contact");

/** The category of a signal type given the loaded catalog (falls back so an unknown type still renders). */
export const categoryOfType = (catalog, type) => {
  if (type === "custom") return "Custom";
  const def = (catalog || []).find(d => d.id === type);
  return (def && def.category) || "Engagement";
};
export const familyOfType = (catalog, type) => familyOf(categoryOfType(catalog, type));

/** Catalog items of kind "event" start agents; only signals (or items with no kind yet) belong in the Library. */
export const isSignal = def => !!def && (!def.kind || def.kind === "signal");

/** "contact" | "company" | "free": the server's costUnit, else worked out from the older cost figure. */
export const costUnitOf = def => {
  if (def && def.costUnit) return def.costUnit;
  if (!def || !def.creditsPerMonthPer100Records) return "free";
  return def.entityType === "ACCOUNT" ? "company" : "contact";
};

/** Free signals (your own activity) are on for everyone: never "Track", always tracked. */
export const isAlwaysOn = def => !!def && (!!def.alwaysOnSignalId || (costUnitOf(def) === "free" && def.available !== false));

/**
 * An agent trigger value for a free signal whose system saved signal the catalog didn't name: the type, prefixed, so
 * the builder sends {signalType} and the server binds it on save.
 */
export const ALWAYS_ON_TYPE_PREFIX = "type:";
export const alwaysOnValue = def => def.alwaysOnSignalId || `${ALWAYS_ON_TYPE_PREFIX}${def.id}`;
export const typeOfAlwaysOnValue = value =>
  String(value || "").startsWith(ALWAYS_ON_TYPE_PREFIX) ? String(value).slice(ALWAYS_ON_TYPE_PREFIX.length) : "";

/** The saved signal behind an agent trigger value: a row from /saved, else a free always-on one built from the catalog. */
export const savedOrAlwaysOn = (saved, catalog, value) => {
  if (!value) return null;
  const row = (saved || []).find(s => (s.signal || {}).id === value);
  if (row) return row;
  const typed = typeOfAlwaysOnValue(value);
  const def = (catalog || []).find(d => (typed ? d.id === typed : d.alwaysOnSignalId === value));
  return def ? { signal: { id: value, signalType: def.id, status: "ACTIVE" }, displayName: def.label, scopeLabel: "the people in your sequences", agents: [], alwaysOn: true } : null;
};

/** "free" | "standard" | "deep": the server's tier, else free or standard from the cost unit. */
export const tierOf = def => (def && def.tier) || (costUnitOf(def) === "free" ? "free" : "standard");

/** Credits one record costs per check: the server's creditsPerUnit, else 2 for deep, 0 free, 1 otherwise. */
export const creditsPerUnitOf = def => {
  if (def && def.creditsPerUnit != null) return Number(def.creditsPerUnit) || 0;
  const tier = tierOf(def);
  return tier === "free" ? 0 : tier === "deep" ? 2 : 1;
};

export const costText = def => {
  const unit = costUnitOf(def);
  const per = creditsPerUnitOf(def);
  return unit === "free" || per === 0 ? "Free" : `${per} credit${per === 1 ? "" : "s"} per ${unit}, each check`;
};

// Signals whose findings are ranked against the seller profile.
export const AI_RANKED_TYPES = ["hiring_intel", "competitors"];

/** A card's badges: Free when it costs nothing, plus at most one of Deep intel or AI-ranked for you. */
export const badgesOf = def => {
  const out = [];
  if (tierOf(def) === "free" || costUnitOf(def) === "free") out.push({ key: "free", label: "Free" });
  if (tierOf(def) === "deep") out.push({ key: "deep", label: "Deep intel" });
  else if (def && AI_RANKED_TYPES.includes(def.id)) out.push({ key: "ai", label: "AI-ranked for you" });
  return out;
};

/**
 * {reads, gives, example} when the server sent the three-part description (`details`, or `description` as an
 * object), else null and the single line stands.
 */
export const descriptionParts = def => {
  const d = def && (def.details || def.descriptionParts || def.description);
  return d && typeof d === "object" && (d.reads || d.gives) ? { reads: d.reads || "", gives: d.gives || "", example: d.example || "" } : null;
};

/** The one-line description, whichever shape the server sent. */
export const descriptionText = def => {
  if (!def) return "";
  if (typeof def.description === "string") return def.description;
  const p = descriptionParts(def);
  return p ? p.gives || p.reads : "";
};

/** Credits for `records` at `perUnit` each: per check, and per month at `checks` checks a month. */
export const creditMath = (records, perUnit, checks) => {
  if (records == null) return { perCheck: null, perMonth: null };
  const perCheck = Number(records) * perUnit;
  return { perCheck, perMonth: perCheck * checks };
};

/** A count as the server sent it: exact, or "N+" only when the response says it stopped counting (`capped`). */
export const countText = (v, capped) => (v == null ? "–" : `${Number(v).toLocaleString()}${capped ? "+" : ""}`);

/** "12 min ago", "3 h ago", "Yesterday", or a short date: whatever the API sent (ISO string or epoch ms). */
export const timeAgo = value => {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const initials = name =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .map(p => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/* ───────── competitors ───────── */

/** Only direct competitors are shown; adjacent and partner names stay in the seller profile. */
// Direct competitors from the profile, plus any the member added themselves.
export const directCompetitors = list => (Array.isArray(list) ? list : []).filter(c => c && (c.bucket === "direct" || c.source === "user"));
/** A real company with no LinkedIn page: not our failure, there is simply nothing to read. */
export const NO_PAGE_TEXT = "No LinkedIn page, so there are no posts to read";

/** What a competitor is doing, in the order the panel shows it. `short` leads the list row ("Products: ..."). */
export const MOVE_KINDS = [
  { kind: "teams", label: "Teams they're building", short: "Teams" },
  { kind: "products", label: "Products they're shipping", short: "Products" },
  { kind: "direction", label: "Where they're headed", short: "Direction" },
  { kind: "customers", label: "Customers they're winning", short: "Customers" },
  { kind: "leadership", label: "Leadership they're adding", short: "Leadership" },
  { kind: "markets", label: "Markets they're entering", short: "Markets" }
];
// Kinds cached before the six; event and other were never moves.
const LEGACY_MOVE = { hiring: "teams", launch: "products", pricing: "direction", positioning: "direction", customer_win: "customers" };
export const moveKindOf = kind => (MOVE_KINDS.some(k => k.kind === kind) ? kind : LEGACY_MOVE[kind] || null);
const kindInfo = kind => MOVE_KINDS.find(k => k.kind === moveKindOf(kind)) || null;

/** The moves grouped by kind, only kinds with a move, in MOVE_KINDS order: [{kind, label, items}]. */
export const movesOf = data => {
  const insights = (data && data.insights) || [];
  return MOVE_KINDS.map(k => ({ kind: k.kind, label: k.label, items: insights.filter(i => i && moveKindOf(i.kind) === k.kind) }))
    .filter(g => g.items.length);
};

/** "Products: launched a Power Dialer"; the headline alone when its kind is unknown. */
export const topMoveText = (kind, headline) => {
  const h = String(headline || "").trim();
  if (!h) return "";
  const k = kindInfo(kind);
  return k ? `${k.short}: ${/^[A-Z][A-Z]/.test(h) ? h : h.charAt(0).toLowerCase() + h.slice(1)}` : h;
};

const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
/** What a row shows once insights are in: the top move, posts in the last 30 days, and when it was read. */
export const competitorSummary = (data, now = Date.now()) => {
  const d = data || {};
  const first = (d.insights || [])[0];
  const posts = (d.posts || []).filter(p => {
    const t = new Date(p && p.postedAt).getTime();
    return !isNaN(t) && now - t <= MONTH_MS;
  });
  return { headline: first ? topMoveText(first.kind, first.headline) : "", postsLast30: posts.length, updatedAt: d.generatedAt || null, reason: d.reason || null };
};

/** Cuts long post text at a word boundary. */
export const trimText = (text, max = 220) => {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ") > max * 0.6 ? cut.lastIndexOf(" ") : max).replace(/[,.;:]$/, "")}…`;
};

/** True while the list row still has nothing cached for its posts (so the page reads them itself). */
// A row being re-looked-up that still has its old domain renders (and reads) as before; only one with no domain waits.
export const isFinding = c => !!(c && c.resolving && !c.domain);
export const needsInsights = c => !!(c && c.domain && c.linkedinUrl && c.insightsUpdatedAt == null && c.postsLast30Days == null);

/**
 * What a competitor row shows: from insights read this session when there are any, else from the list's cached
 * fields. {text, pending, quiet, posts, updatedAt}; `pending` text renders as a soft placeholder.
 */
export const competitorRow = (c, entry) => {
  const pending = text => ({ text, pending: true, quiet: true, posts: null, updatedAt: null });
  const quiet = text => ({ text, pending: false, quiet: true, posts: null, updatedAt: null });
  if (!c) return quiet("");
  if (isFinding(c)) return pending("Finding their company page…");
  if (!c.domain || !c.linkedinUrl) return quiet(NO_PAGE_TEXT);
  if (entry && entry.loading) return pending("Reading what they're up to…");
  if (entry && entry.data) {
    const s = competitorSummary(entry.data);
    if (s.reason === "no_linkedin_page") return quiet(NO_PAGE_TEXT);
    if (s.reason === "no_linkedin_account") return quiet("Connect a LinkedIn account to read their posts");
    if (s.reason === "unavailable") return quiet("Couldn't load posts right now");
    const n = (entry.data.posts || []).length;
    const any = n || (entry.data.peoplePosts || []).length;
    return { text: !any ? "No posts in the last 30 days" : s.headline || "No clear move this month", pending: false, quiet: !s.headline, posts: s.postsLast30, updatedAt: s.updatedAt };
  }
  if (needsInsights(c)) return pending("Reading what they're up to…");
  const posts = c.postsLast30Days == null ? null : Number(c.postsLast30Days);
  const top = topMoveText(c.latestKind, c.latestHeadline);
  const text = posts === 0 && !top ? "No posts in the last 30 days" : top || "No clear move this month";
  return { text, pending: false, quiet: !top, posts, updatedAt: c.insightsUpdatedAt || null };
};

/**
 * The main button for one feed entry, shared by the feed and a tracker's table: Open for an account, Draft email when
 * the contact has an address, Find email (their page) when they don't, else Draft email on their page.
 */
export const mainActionOf = ({ isAccount, email, contactId } = {}) =>
  isAccount
    ? { kind: "open", label: "Open" }
    : email
      ? { kind: "draft_email", label: "Draft email" }
      : contactId
        ? { kind: "find_email", label: "Find email" }
        : { kind: "page", label: "Draft email" };

/** The list a "you keep adding these to a list" habit used: the newest acted entry of that type that went to a list. */
export const habitListId = (items, signalType) => {
  const hit = (items || []).find(i => {
    const e = i.event || {};
    return e.signalType === signalType && (i.actionType || e.actionType) === "add_to_list" && (i.actionTargetId || e.actionTargetId);
  });
  return hit ? hit.actionTargetId || hit.event.actionTargetId : "";
};

/** What one check of an existing scope costs, in the estimate's own numbers, so 0 credits never reads as a mistake. */
export const scopeCostLine = (est, noun, count = v => Number(v).toLocaleString(), perUnit = 1) => {
  const records = est && est.records;
  if (records == null) return "Total not available yet";
  const perCheck = (est.newRecords != null ? est.newRecords : records) * perUnit;
  const watched = est.alreadyWatched || 0;
  if (watched > 0) {
    return `${count(records)} ${noun} · ${count(watched)} already watched by another signal · ${count(perCheck)} credits each check`;
  }
  return `${count(records)} ${noun} · ${count(perCheck)} credits each check`;
};

/**
 * The signal name behind a "signal.saved" agent trigger config ({signalType} | {savedSignalId} | {alwaysOnSignalId}),
 * from the member's saved signals or the catalog. "" when neither has loaded or knows it.
 */
export const signalTriggerName = (config, saved, catalog) => {
  const c = config || {};
  const labelOf = type => ((catalog || []).find(d => d.id === type) || {}).label || "";
  if (c.signalType) return labelOf(c.signalType);
  const row = savedOrAlwaysOn(saved, catalog, c.savedSignalId || c.alwaysOnSignalId);
  return (row && (row.displayName || labelOf((row.signal || {}).signalType))) || "";
};

/** Words people search with that a signal's own label and copy may not use ("hiring" finds Headcount growth). */
export const SIGNAL_KEYWORDS = {
  positive_reply: ["interested", "reply", "replied", "responded", "warm", "yes"],
  hot_opens: ["opens", "opened", "engaged", "reading", "interest", "email opens"],
  link_click: ["click", "clicked", "visited", "link"],
  connection_accepted: ["linkedin", "connected", "accepted", "connection"],
  went_quiet: ["ghosted", "silent", "no reply", "stopped replying", "cold"],
  came_back: ["returned", "re-engaged", "back", "revisited"],
  job_change: ["new job", "moved", "switched", "new role", "promotion", "promoted", "changed jobs"],
  champion_move: ["champion", "past customer", "former customer", "old customer", "advocate", "user moved"],
  headcount_growth: ["hiring", "growth", "growing", "team size", "headcount", "expanding", "new hires"],
  hiring_intel: ["hiring", "jobs", "roles", "tech stack", "skills", "hiring manager"],
  headcount_decline: ["layoffs", "shrinking", "downsizing", "team size", "cuts"],
  new_leader: ["new executive", "new vp", "new head", "leadership change", "exec"],
  left_company: ["left", "departed", "quit", "gone", "no longer there"]
};

/** True when a Library query matches a signal by its label, copy or keywords. */
export const signalMatches = (def, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const words = (SIGNAL_KEYWORDS[def.id] || []).concat(def.keywords || []);
  const p = descriptionParts(def) || {};
  return `${def.label} ${descriptionText(def)} ${p.reads || ""} ${p.gives || ""} ${p.example || ""} ${def.example || ""} ${words.join(" ")}`.toLowerCase().includes(q);
};

/* ───────── hiring intel ───────── */

export const HIRING = "hiring_intel";

export const firstNameOf = name => String(name || "").trim().split(/\s+/)[0] || "";

/** The first `max` non-empty entries and how many more there are ("+N"). */
export const chipsOf = (list, max = 5) => {
  const all = (Array.isArray(list) ? list : []).filter(Boolean);
  return { shown: all.slice(0, max), more: Math.max(0, all.length - max) };
};

/**
 * A hiring intel entry's payload, null-safe (the server sends null, not undefined): the summary, what they are
 * building, tech and skills, the roles, the hiring owner (the payload's, else the first role's) and a post to open.
 */
export const hiringOf = item => {
  const e = (item && item.event) || {};
  const p = e.payload || {};
  const roles = (Array.isArray(p.roles) ? p.roles : []).filter(Boolean);
  const named = o => (o && o.name ? o : null);
  // A recruiter or a peer (below manager) posted it: the poster is not the one to sell to, so there is no owner
  // and the persona leads.
  const ownerIsRecruiter = !!(p.ownerIsRecruiter || e.ownerIsRecruiter);
  const ownerIsPeer = !ownerIsRecruiter && !!(p.ownerIsPeer || e.ownerIsPeer);
  const stepsAside = ownerIsRecruiter || ownerIsPeer;
  const owner = stepsAside ? null : named(p.owner) || named((roles.find(r => named(r.owner)) || {}).owner);
  return {
    summary: p.summary || e.headline || "",
    initiative: p.initiative || "",
    technologies: Array.isArray(p.technologies) ? p.technologies : [],
    skills: Array.isArray(p.skills) ? p.skills : [],
    roles,
    owner,
    postUrl: (roles.find(r => r.postUrl) || {}).postUrl || p.postUrl || "",
    why: p.whyItMatters && p.whyItMatters.text ? { text: p.whyItMatters.text, action: p.whyItMatters.action || "" } : null,
    persona: p.suggestedPersona || "",
    ownerIsRecruiter,
    ownerIsPeer,
    poster: stepsAside ? named(p.poster || e.poster) : null,
    isCompetitor: !!(p.isCompetitor || e.isCompetitor),
    accountName: p.accountName || e.accountName || ""
  };
};

/* ───────── seller profile ───────── */

export const domainOf = url => String(url || "").trim().replace(/^[a-z]+:\/\//i, "").replace(/^www\./i, "").split(/[/?#]/)[0].toLowerCase();

// The signals read through a seller profile (what the seller sells decides what matters). The rest ignore it.
export const SELLER_PROFILE_TYPES = ["hiring_intel", "headcount_growth", "headcount_decline"];
export const usesSellerProfile = type => SELLER_PROFILE_TYPES.includes(type);

/** "For: Adapt.io" on a tracked account signal; empty for the rest or when the server sent no name. */
export const sellerProfileFor = row => {
  const s = (row && row.signal) || {};
  const name = (row && row.sellerProfileName) || s.sellerProfileName;
  return usesSellerProfile(s.signalType) && name ? `For: ${name}` : "";
};

/* ───────── suggested drafts ───────── */

// The entries that come with a suggested email (a competitor's own hiring never does).
export const DRAFT_TYPES = ["hiring_intel", "came_back", "went_quiet", "hot_opens", "job_change", "champion_move", "custom"];

/** "ready" | "pending" | "none" for one feed entry; a ready status with nothing to show reads as none. */
export const draftStateOf = item => {
  const e = (item && item.event) || {};
  if (!DRAFT_TYPES.includes(e.signalType) || hiringOf(item).isCompetitor) return "none";
  const status = item.draftStatus || "none";
  const d = item.draft || {};
  if (status === "ready" && !(d.subject || d.body)) return "none";
  return status === "ready" || status === "pending" ? status : "none";
};

/** Who a draft is for: the row's own person, with the draft's recipient (non-empty fields only) on top. */
export const draftRecipient = (base, draft) => {
  const to = (draft && draft.to) || {};
  const out = { ...(base || {}) };
  Object.keys(to).forEach(k => {
    if (to[k]) out[k] = to[k];
  });
  return out;
};

/** A plain-text draft body as the composer's HTML: paragraphs on blank lines, line breaks kept. HTML passes through. */
// A domain or website as a bare host ("https://www.Acme.com/x" is "acme.com").
const hostOf = d => String(d || "").trim().toLowerCase().replace(/^[a-z]+:\/\//, "").replace(/^www\./, "").split(/[/?#:]/)[0];

/** The email's domain when it is not at the account's domain or one of its alias domains (subdomains count), else "". */
export const offDomain = (email, domain, aliases) => {
  const at = hostOf(String(email || "").split("@")[1]);
  const own = [domain, ...(aliases || [])].map(hostOf).filter(Boolean);
  if (!at || !own.length) return "";
  return own.some(d => at === d || at.endsWith(`.${d}`)) ? "" : at;
};

/** A persona draft picked for a named person: a leading "Hi there," or "Hello there," becomes "Hi <first name>,". */
export const greetByName = (body, name) => {
  const first = String(name || "").trim().split(/\s+/)[0];
  return first && body ? String(body).replace(/^(\s*)(hi|hello) there,/i, `$1Hi ${first},`) : body;
};

export const draftBodyHtml = text => {
  const s = String(text || "");
  if (/<\/?(div|p|br|span|a|b|i|u|strong|em|ul|ol|li)\b[^>]*>/i.test(s)) return s;
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.trim().split(/\n\s*\n/).map(p => `<div>${p.replace(/\n/g, "<br/>")}</div>`).join("<div><br/></div>");
};
